import { Box, Stack, Typography, useTheme } from '@mui/material';
import DOMPurify from 'dompurify';
import { FiMessageSquare, FiUser } from 'react-icons/fi';
import { MdOutlineEmail } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import type { InteractionNotification } from '@/apis/business/agent/callLogs/types';
import { titleCase } from 'utils/helperFunctions';

const badgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: 10,
  fontWeight: 500,
  borderRadius: 6,
  px: 1.5,
  whiteSpace: 'nowrap'
} as const;

function getChannelBadge(channel: InteractionNotification['channel'], theme: any) {
  switch (channel) {
    case 'email':
      return (
        <Box sx={{ ...badgeSx, color: theme.palette.purple.main, bgcolor: theme.palette.purple.light }}>
          <MdOutlineEmail size={10} />
          EMAIL
        </Box>
      );
    case 'sms':
    default:
      return (
        <Box sx={{ ...badgeSx, color: theme.palette.blue.main, bgcolor: theme.palette.blue.light }}>
          <FiMessageSquare size={10} />
          SMS
        </Box>
      );
  }
}

function getRecipientBadge(recipientType: InteractionNotification['recipient_type'], theme: any) {
  switch (recipientType) {
    case 'operator':
      return (
        <Box sx={{ ...badgeSx, color: theme.palette.primary.main, bgcolor: theme.palette.primary.light }}>
          <FiUser size={10} />
          Operator
        </Box>
      );
    case 'customer':
    default:
      return (
        <Box
          sx={{
            ...badgeSx,
            color: theme.palette.warning.main,
            bgcolor: theme.palette.warning.light,
            border: `1px solid ${theme.palette.warning.main}`
          }}
        >
          <FiUser size={10} />
          Customer
        </Box>
      );
  }
}

function getCallPhaseBadge(callPhase: InteractionNotification['call_phase'], theme: any) {
  switch (callPhase) {
    case 'during_call':
      return (
        <Box sx={{ ...badgeSx, color: theme.palette.secondary.main, bgcolor: theme.palette.secondary.light }}>
          During Call
        </Box>
      );
    case 'after_call':
    default:
      return (
        <Box sx={{ ...badgeSx, color: theme.palette.secondary.main, bgcolor: theme.palette.secondary.light }}>
          Post Call
        </Box>
      );
  }
}

function getStatusBadge(status: InteractionNotification['status'], theme: any) {
  switch (status) {
    case 'sent':
      return (
        <Box sx={{ ...badgeSx, color: theme.palette.success.main, bgcolor: theme.palette.success.light }}>Sent</Box>
      );
    case 'delivered':
      return (
        <Box sx={{ ...badgeSx, color: theme.palette.success.main, bgcolor: theme.palette.success.light }}>
          Delivered
        </Box>
      );
    case 'failed':
      return <Box sx={{ ...badgeSx, color: theme.palette.error.main, bgcolor: theme.palette.error.light }}>Failed</Box>;
    default:
      return (
        <Box sx={{ ...badgeSx, color: theme.palette.secondary.main, bgcolor: theme.palette.secondary.light }}>
          {titleCase(status)}
        </Box>
      );
  }
}

export default function NotificationCard({
  notification,
  formatDate
}: {
  notification: InteractionNotification;
  formatDate: (date: string) => string;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        p: 2,
        mb: 2
      }}
    >
      <Stack direction='row' gap={0.75} flexWrap='wrap' mb={1.5}>
        {getChannelBadge(notification.channel, theme)}
        {notification.recipient_type && getRecipientBadge(notification.recipient_type, theme)}
        {notification.call_phase && getCallPhaseBadge(notification.call_phase, theme)}
        {getStatusBadge(notification.status, theme)}
      </Stack>

      <Stack direction='column' mb={1}>
        <Typography color='text.secondary' fontSize={12}>
          To:
        </Typography>
        <Typography fontSize={13} lineHeight={1.5}>
          {notification.channel === 'sms' ? notification.to_number : notification.to}
        </Typography>
      </Stack>

      {notification.subject && (
        <Stack direction='column' mb={1}>
          <Typography color='text.secondary' fontSize={12}>
            Subject:
          </Typography>
          <Typography fontSize={13} lineHeight={1.5}>
            {notification.subject}
          </Typography>
        </Stack>
      )}

      <Stack direction='column' mb={2}>
        <Typography color='text.secondary' fontSize={12}>
          Message:
        </Typography>
        <Box
          border='1px solid #ECECEC'
          bgcolor='#f6f5f5'
          mt={1}
          padding={2}
          borderRadius={1}
          sx={{
            typography: 'body2',

            fontSize: 13,
            lineHeight: 1.5,
            '& p': {
              m: 0
            },
            '& p + p': { mt: 1 }
          }}
        >
          {notification.channel === 'sms' ? (
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>{notification.body}</ReactMarkdown>
          ) : (
            <Box
              component='div'
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(notification.message ?? '')
              }}
            />
          )}
        </Box>
      </Stack>

      <Typography fontSize={12} color='text.secondary'>
        {formatDate(notification.created_at)}
      </Typography>
    </Box>
  );
}
